/**
 * BhiSection.jsx — iter840 (Feb 2026)
 *
 * Extracted from BreastHealthIntelligencePage.jsx (was ~30 lines inline).
 * Collapsible section wrapper with persistent per-testid state.
 *
 * iter840 additions:
 *   • Personal View mode — on FIRST visit (localStorage flag absent), all
 *     non-essential sections auto-collapse. User can override via chevron
 *     or the global "Разгъни всички" button.
 *   • `defaultOpen` prop lets the parent mark essential sections that
 *     should stay open on first visit even in Personal View mode.
 *   • Reacts to a `bhi:collapse-all` / `bhi:expand-all` custom window
 *     event so the global toggle can broadcast to every section without
 *     prop drilling.
 */
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const PERSONAL_VIEW_KEY = "bhi_personal_view_v1_seen";

// iter840 — Snapshot the "seen" flag ONCE at module load, BEFORE any
// PersonalViewToggle effect can write to it. This eliminates the race
// where the toggle's useEffect ran before BhiSection children mounted
// (all non-essential sections would then see seenBefore=true and default
// to expanded, defeating the first-visit auto-collapse UX).
const INITIAL_SEEN_BEFORE = (() => {
  try { return localStorage.getItem(PERSONAL_VIEW_KEY) === "1"; }
  catch { return false; }
})();

/** Read the collapsed flag for this testid, applying Personal View defaults. */
function readInitialCollapsed(testid, defaultOpen) {
  try {
    const key = `bhi_section_collapsed_${testid || "unknown"}`;
    const stored = localStorage.getItem(key);
    if (stored != null) return stored === "1";
    // First-visit path (uses module-load snapshot, immune to write races).
    if (!INITIAL_SEEN_BEFORE && !defaultOpen) return true;
    return false;
  } catch { return false; }
}

export default function BhiSection({
  icon: Icon, title, help, children, testid, methodologyInfo, defaultOpen = false,
  wrapClassName = "mb-8",  // iter1032 — dashboard layout injects grid col-span classes here
  style,                   // iter1016 — CSS order за подреждането от админ
}) {
  const storageKey = `bhi_section_collapsed_${testid || "unknown"}`;
  const [collapsed, setCollapsed] = useState(() => readInitialCollapsed(testid, defaultOpen));

  const setPersistent = (next) => {
    setCollapsed(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* private mode */ }
  };

  const toggle = () => setPersistent(!collapsed);

  // Listen for global expand/collapse broadcasts (iter840).
  useEffect(() => {
    const onExpand   = () => setPersistent(false);
    const onCollapse = () => setPersistent(true);
    // iter998 — Smart-expand: open only sections whose testid is in detail.list
    const onExpandByTestid = (e) => {
      const list = e?.detail?.list || [];
      if (Array.isArray(list) && testid && list.includes(testid)) setPersistent(false);
    };
    window.addEventListener("bhi:expand-all", onExpand);
    window.addEventListener("bhi:collapse-all", onCollapse);
    window.addEventListener("bhi:expand-testids", onExpandByTestid);
    return () => {
      window.removeEventListener("bhi:expand-all", onExpand);
      window.removeEventListener("bhi:collapse-all", onCollapse);
      window.removeEventListener("bhi:expand-testids", onExpandByTestid);
    };
  }, [testid]);

  return (
    <section className={wrapClassName} data-testid={testid}
      style={{ scrollMarginTop: 96, ...(style || {}) }}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {Icon && <Icon className="w-5 h-5 text-pink-500" />}
        <h2 className="text-lg sm:text-xl font-bold">{title}</h2>
        {methodologyInfo}
        <button
          type="button"
          onClick={toggle}
          data-testid={`${testid}-collapse`}
          aria-label={collapsed ? "Разгъни секцията" : "Свий секцията"}
          title={collapsed ? "Разгъни" : "Свий"}
          className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 dark:border-slate-700 text-slate-500 hover:bg-pink-100 hover:text-pink-600 dark:hover:bg-pink-500/20 transition-colors"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>
      {help && !collapsed && <p className="text-xs opacity-60 mb-3">{help}</p>}
      {!collapsed && children}
      {collapsed && (
        <div className="text-[11px] opacity-50 italic mt-1" data-testid={`${testid}-collapsed-hint`}>
          Секцията е свита. Кликнете стрелката, за да разгънете.
        </div>
      )}
    </section>
  );
}

/**
 * Marks Personal View as "seen" so subsequent visits use per-section state.
 * Call once from the parent page on mount.
 */
export function markPersonalViewSeen() {
  try { localStorage.setItem(PERSONAL_VIEW_KEY, "1"); } catch { /* noop */ }
}

/** Broadcasts a global expand/collapse to every mounted BhiSection. */
export function expandAllSections() {
  window.dispatchEvent(new CustomEvent("bhi:expand-all"));
}
export function collapseAllSections() {
  window.dispatchEvent(new CustomEvent("bhi:collapse-all"));
}

/**
 * iter998 — Smart-expand: open ONLY sections whose testid is in `list`.
 * Non-listed sections keep their current collapsed state (no mass toggle).
 * Also scrolls to the first section in `list` for immediate context.
 */
export function expandSectionsByTestids(list = [], opts = {}) {
  const clean = Array.isArray(list) ? list.filter(Boolean) : [];
  if (!clean.length) return;
  window.dispatchEvent(new CustomEvent("bhi:expand-testids", { detail: { list: clean } }));
  if (opts.scroll !== false) {
    // Allow the section to render its body before scrolling.
    setTimeout(() => {
      const first = clean[0];
      const el = document.querySelector(`[data-testid="${first}"]`);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 120);
  }
}
